import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore';

import contactsJson from 'utils/contacts.json';
import { db } from 'utils/firebaseConfig';
import { appEnv } from 'config/env';
import type { FamilyContact, FamilyMember } from 'types/domain';

const CONTACTS_COLLECTION = 'contacts';

export interface SeedContactsResult {
  importedCount: number;
}

export interface MigrateContactImagesResult {
  uploadedCount: number;
  updatedContacts: number;
  failedCount: number;
}

type UploadImageScope = 'family' | 'member';

interface UploadContactImageParams {
  contactId: number;
  scope: UploadImageScope;
  memberId?: number;
  file: File;
}

const sanitizeFileName = (name: string): string => {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
};

const isRemoteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const getCloudinaryUploadEndpoint = (): string => {
  return `https://api.cloudinary.com/v1_1/${appEnv.cloudinary.cloudName}/image/upload`;
};

const uploadBlobToCloudinary = async ({
  blob,
  fileName,
  folder,
}: {
  blob: Blob;
  fileName: string;
  folder: string;
}): Promise<string> => {
  const formData = new FormData();
  formData.append('file', blob, fileName);
  formData.append('upload_preset', appEnv.cloudinary.uploadPreset);
  formData.append('folder', folder);
  formData.append('public_id', fileName.substring(0, fileName.lastIndexOf('.')) || fileName);

  if (appEnv.cloudinary.apiKey) {
    formData.append('api_key', appEnv.cloudinary.apiKey);
  }

  const response = await fetch(getCloudinaryUploadEndpoint(), {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorPayload = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(errorPayload?.error?.message || 'Cloudinary upload failed.');
  }

  const data = (await response.json()) as { secure_url?: string };
  if (!data.secure_url) {
    throw new Error('Cloudinary response missing secure_url.');
  }

  return data.secure_url;
};

const normalizeMember = (member: FamilyMember): FamilyMember => ({
  ...member,
  id: Number(member.id),
  mobile: member.mobile || '',
  alternateNumber: member.alternateNumber || '',
});

const normalizeContact = (contact: FamilyContact): FamilyContact => ({
  ...contact,
  id: Number(contact.id),
  mobile: contact.mobile || '',
  familyDetails: Array.isArray(contact.familyDetails)
    ? contact.familyDetails.map((member) => normalizeMember(member))
    : [],
});

export const getContactsFromFirestore = async (): Promise<FamilyContact[]> => {
  const contactsRef = collection(db, CONTACTS_COLLECTION);
  const contactsQuery = query(contactsRef, orderBy('familyHeadName'));
  const querySnapshot = await getDocs(contactsQuery);

  if (querySnapshot.empty) {
    return [];
  }

  return querySnapshot.docs.map((snapshot) => normalizeContact(snapshot.data() as FamilyContact));
};

export const getContacts = async (): Promise<FamilyContact[]> => {
  return getContactsFromFirestore();
};

export const upsertContact = async (contact: FamilyContact): Promise<FamilyContact> => {
  const normalizedContact = normalizeContact(contact);
  const contactDocRef = doc(db, CONTACTS_COLLECTION, String(normalizedContact.id));

  await setDoc(contactDocRef, normalizedContact, { merge: true });
  return normalizedContact;
};

export const removeContact = async (contactId: FamilyContact['id']): Promise<void> => {
  const contactDocRef = doc(db, CONTACTS_COLLECTION, String(contactId));
  await deleteDoc(contactDocRef);
};

export const upsertFamilyMember = async (
  contactId: FamilyContact['id'],
  member: FamilyMember
): Promise<FamilyMember[]> => {
  const contactDocRef = doc(db, CONTACTS_COLLECTION, String(contactId));
  const contactSnapshot = await getDoc(contactDocRef);

  if (!contactSnapshot.exists()) {
    throw new Error('Family contact not found.');
  }

  const existingContact = normalizeContact(contactSnapshot.data() as FamilyContact);
  const normalizedMember = normalizeMember(member);
  const existingMembers = existingContact.familyDetails || [];
  const memberIndex = existingMembers.findIndex((item) => Number(item.id) === Number(normalizedMember.id));

  const nextMembers = [...existingMembers];
  if (memberIndex >= 0) {
    nextMembers[memberIndex] = normalizedMember;
  } else {
    nextMembers.push(normalizedMember);
  }

  await setDoc(contactDocRef, { familyDetails: nextMembers }, { merge: true });
  return nextMembers;
};

export const removeFamilyMember = async (
  contactId: FamilyContact['id'],
  memberId: FamilyMember['id']
): Promise<FamilyMember[]> => {
  const contactDocRef = doc(db, CONTACTS_COLLECTION, String(contactId));
  const contactSnapshot = await getDoc(contactDocRef);

  if (!contactSnapshot.exists()) {
    throw new Error('Family contact not found.');
  }

  const existingContact = normalizeContact(contactSnapshot.data() as FamilyContact);
  const nextMembers = (existingContact.familyDetails || []).filter(
    (item) => Number(item.id) !== Number(memberId)
  );

  await setDoc(contactDocRef, { familyDetails: nextMembers }, { merge: true });
  return nextMembers;
};

export const seedContactsFromJsonToFirestore = async (): Promise<SeedContactsResult> => {
  const contacts = (contactsJson as FamilyContact[]).map((contact) => normalizeContact(contact));
  const batch = writeBatch(db);

  contacts.forEach((contact) => {
    const contactDocRef = doc(db, CONTACTS_COLLECTION, String(contact.id));
    batch.set(contactDocRef, contact, { merge: true });
  });

  await batch.commit();

  return {
    importedCount: contacts.length,
  };
};

export const uploadContactImage = async ({
  contactId,
  scope,
  memberId,
  file,
}: UploadContactImageParams): Promise<string> => {
  if (!appEnv.cloudinary.cloudName || !appEnv.cloudinary.uploadPreset) {
    throw new Error('Cloudinary is not configured. Set VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET.');
  }

  const extension = file.name.split('.').pop() || 'jpg';
  const safeName = sanitizeFileName(file.name.replace(`.${extension}`, ''));
  const fileName = `${Date.now()}-${safeName}.${extension}`;
  const basePath = `${appEnv.cloudinary.folder}/${contactId}`;
  const folderPath =
    scope === 'family'
      ? `${basePath}/family/${fileName}`
      : `${basePath}/members/${memberId || 'unknown'}/${fileName}`;
  return uploadBlobToCloudinary({
    blob: file,
    fileName,
    folder: folderPath.substring(0, folderPath.lastIndexOf('/')),
  });
};

export const migrateExistingContactImagesToCloudinary = async (): Promise<MigrateContactImagesResult> => {
  const contacts = await getContactsFromFirestore();
  let uploadedCount = 0;
  let updatedContacts = 0;
  let failedCount = 0;

  for (const contact of contacts) {
    let hasChanges = false;
    let nextContact: FamilyContact = { ...contact };

    if (contact.photoFile && !isRemoteUrl(contact.photoFile)) {
      try {
        const sourceResponse = await fetch(`/contactImages/${contact.photoFile}`);
        if (!sourceResponse.ok) {
          throw new Error('Source image not found.');
        }

        const blob = await sourceResponse.blob();
        const extension = contact.photoFile.split('.').pop() || 'jpg';
        const fileName = `${Date.now()}-family-${contact.id}.${extension}`;
        const uploadedUrl = await uploadBlobToCloudinary({
          blob,
          fileName,
          folder: `${appEnv.cloudinary.folder}/${contact.id}/family`,
        });

        nextContact.photoFile = uploadedUrl;
        hasChanges = true;
        uploadedCount += 1;
      } catch (error) {
        console.error(`Failed to migrate family image for contact ${contact.id}`, error);
        failedCount += 1;
      }
    }

    const nextMembers = (contact.familyDetails || []).map((member) => ({ ...member }));
    for (let i = 0; i < nextMembers.length; i += 1) {
      const member = nextMembers[i];
      if (!member.photoFile || isRemoteUrl(member.photoFile)) {
        continue;
      }

      try {
        const sourceResponse = await fetch(`/contactImages/${member.photoFile}`);
        if (!sourceResponse.ok) {
          throw new Error('Source image not found.');
        }

        const blob = await sourceResponse.blob();
        const extension = member.photoFile.split('.').pop() || 'jpg';
        const fileName = `${Date.now()}-member-${contact.id}-${member.id}.${extension}`;
        const uploadedUrl = await uploadBlobToCloudinary({
          blob,
          fileName,
          folder: `${appEnv.cloudinary.folder}/${contact.id}/members/${member.id}`,
        });

        nextMembers[i] = {
          ...member,
          photoFile: uploadedUrl,
        };
        hasChanges = true;
        uploadedCount += 1;
      } catch (error) {
        console.error(`Failed to migrate member image for contact ${contact.id}, member ${member.id}`, error);
        failedCount += 1;
      }
    }

    if (hasChanges) {
      nextContact = {
        ...nextContact,
        familyDetails: nextMembers,
      };
      await upsertContact(nextContact);
      updatedContacts += 1;
    }
  }

  return {
    uploadedCount,
    updatedContacts,
    failedCount,
  };
};
